import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { IUser } from '../auth/user';
import { NavbarService } from '../navbar/navbar.service';
import { AppLocaleStorageService } from '../util/local-storage.service';

export type Theme = 'light' | 'dark';

/**
 * Light / dark mode, set and saved the way the legacy navbar did it: the last choice in this browser
 * (foodnotify.viewMode) until the user has loaded, then the user's saved designMode; a switch is saved with
 * GET /app/switch-design-mode (the design_mode setting, which the Twig and React apps read as well).
 * On <html> it sets color-mode (the legacy light / dark variables in styles.scss), the old theme-light class and
 * data-ground: the 2027 content ground in tailwind.css, Steinweiß (light) or Nacht (dark).
 */
@Injectable({ providedIn: 'root' })
export class ThemeService {
  readonly theme$: BehaviorSubject<Theme>;

  constructor(private navbarService: NavbarService, private alss: AppLocaleStorageService) {
    this.theme$ = new BehaviorSubject<Theme>(alss.getItem('viewMode') === 'dark' ? 'dark' : 'light');
    this.apply();
  }

  get theme(): Theme {
    return this.theme$.value;
  }

  /** The saved designMode of the signed-in user (no request: it comes with the user) */
  syncFromUser(user: IUser | null | undefined): void {
    if (user?.designMode === 'light' || user?.designMode === 'dark') {
      this.use(user.designMode);
    }
  }

  /** User choice: the UI switches right away, saving it on the server runs in the background */
  switch(theme: Theme, user?: IUser | null): void {
    if (theme === this.theme) {
      return;
    }
    this.use(theme);
    if (user) {
      user.designMode = theme;
    }
    this.navbarService.updateDesignModeForUser(theme, () => undefined);
  }

  /** Puts the current theme back on <html> after something else changed it */
  apply(): void {
    const html = document.documentElement;
    html.setAttribute('color-mode', this.theme);
    html.setAttribute('data-ground', this.theme === 'dark' ? 'nacht' : 'stein');
    html.classList.toggle('theme-light', this.theme === 'light');
  }

  private use(theme: Theme): void {
    this.alss.setItem('viewMode', theme);
    this.theme$.next(theme);
    this.apply();
  }
}
